/**
 * ExitTicketReviewForm — shown right under a generated Lesson 1 (or
 * later lesson). Teacher splits the class into three buckets:
 *   - 🟢 Mastered
 *   - 🟡 Developing
 *   - 🔴 Still holding the misconception
 *
 * Submitting kicks off Stage 5 (Exit Ticket Review).
 */
import React, { useMemo, useState } from "react";
import { ListChecks, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";

const Bucket = ({ label, hint, value, onChange, accent, testId }) => (
  <div
    className={`rounded-xl border-2 ${accent} p-4 flex flex-col gap-2`}
    data-testid={testId}
  >
    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70">
      {label}
    </p>
    <p className="text-[12px] text-[#4a6984]/85 leading-snug">{hint}</p>
    <div className="relative">
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ""))}
        placeholder="0"
        className="w-full text-center rounded-lg border border-[#4a6984]/20 px-3 py-2 text-lg font-bold text-[#1c2d3d] focus:outline-none focus:ring-2 focus:ring-[#96ded1] bg-white"
        data-testid={`${testId}-input`}
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-[#4a6984]/50 pointer-events-none">
        %
      </span>
    </div>
  </div>
);

const ExitTicketReviewForm = ({ lesson1Activity, onSubmit, submitting }) => {
  const dominant = useMemo(() => {
    const me = (lesson1Activity?.inputs || {}).math_engine || {};
    return me.dominant_misconception || "";
  }, [lesson1Activity]);

  const lessonIndex = useMemo(() => {
    const me = (lesson1Activity?.inputs || {}).math_engine || {};
    const idx =
      me.lesson_index ||
      (lesson1Activity?.output_type === "math_lesson1" ? 1 : 2);
    return Number(idx) || 1;
  }, [lesson1Activity]);

  const lessonLabel = `Lesson ${lessonIndex}`;

  const [mastered, setMastered] = useState("");
  const [developing, setDeveloping] = useState("");
  const [reteach, setReteach] = useState("");
  const [note, setNote] = useState("");

  const numeric = (v) => parseFloat(v) || 0;
  const total = numeric(mastered) + numeric(developing) + numeric(reteach);
  const ok = total >= 95 && total <= 105;

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!ok) {
      toast.error("The three buckets should total roughly 100%.");
      return;
    }
    onSubmit({
      mastered_pct: numeric(mastered),
      developing_pct: numeric(developing),
      reteach_pct: numeric(reteach),
      note: note.trim(),
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="ae-card p-6 md:p-8"
      data-testid="math-engine-exit-review-form"
    >
      <div className="flex items-start gap-3 mb-5">
        <span className="h-10 w-10 rounded-xl bg-[#96ded1]/30 grid place-items-center text-[#1c2d3d] shrink-0">
          <ListChecks className="h-5 w-5" />
        </span>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#4a6984]/70">
            Next step · Read the exit tickets
          </p>
          <h3 className="font-black text-2xl text-[#1c2d3d] tracking-tight">
            What happened on {lessonLabel}'s exit ticket?
          </h3>
          <p className="text-sm text-[#4a6984]/80 mt-1 leading-relaxed max-w-2xl">
            Look at the exit tickets from {lessonLabel} and split your class
            into three rough groups. The engine will tell you whether to{" "}
            <span className="font-bold">move on</span>,{" "}
            <span className="font-bold">hold this ground</span>, or{" "}
            <span className="font-bold">re-teach</span> tomorrow — with a
            concrete plan for whichever call lands.
            {dominant && (
              <>
                {" "}
                {lessonLabel} was anchored on{" "}
                <span className="font-bold">{dominant}</span>; the "still
                holding the misconception" bucket should count students who
                still showed that pattern on the exit ticket.
              </>
            )}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Bucket
          label="🟢 Mastered the WALT"
          hint="Clear, confident demonstration of the learning goal. Place-value language, named strategies, full reasoning."
          value={mastered}
          onChange={setMastered}
          accent="border-[#96ded1] bg-[#96ded1]/10"
          testId="math-engine-exit-mastered"
        />
        <Bucket
          label="🟡 Partial / developing"
          hint="Mixed signals — some right ideas, some shaky. Got there with prompts. Inconsistent across the two questions."
          value={developing}
          onChange={setDeveloping}
          accent="border-amber-200 bg-amber-50/50"
          testId="math-engine-exit-developing"
        />
        <Bucket
          label="🔴 Still holding the misconception"
          hint="The dominant misconception from Lesson 1 is still firing. Needs a re-teach with a different representation."
          value={reteach}
          onChange={setReteach}
          accent="border-red-200 bg-red-50/40"
          testId="math-engine-exit-reteach"
        />
      </div>

      <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
        <span
          className={`text-xs font-bold ${
            ok
              ? "text-[#1c2d3d]"
              : total > 0
              ? "text-amber-700"
              : "text-[#4a6984]/50"
          }`}
          data-testid="math-engine-exit-total"
        >
          Total: {total ? `${Math.round(total)}%` : "—"} (aim for ~100%)
        </span>
      </div>

      <div className="mt-5">
        <label
          htmlFor="exit-review-note"
          className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70 mb-1.5"
        >
          Anything else you noticed in the room today? (Optional)
        </label>
        <textarea
          id="exit-review-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="e.g. the grouping didn't work, one student had a breakthrough, pacing ran long on the Action phase"
          className="w-full rounded-lg border border-[#4a6984]/20 px-3 py-2 text-sm text-[#1c2d3d] focus:outline-none focus:ring-2 focus:ring-[#96ded1] bg-white leading-relaxed"
          data-testid="math-engine-exit-note"
        />
      </div>

      <div className="mt-5 flex flex-wrap gap-2 justify-end">
        <Button
          type="submit"
          disabled={!ok || submitting}
          data-testid="math-engine-exit-submit"
          className="bg-[#4a6984] hover:bg-[#1c2d3d] text-white disabled:opacity-50"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reading…
            </>
          ) : (
            <>
              <ListChecks className="h-4 w-4 mr-2" /> Recommend tomorrow's call
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export default ExitTicketReviewForm;
